import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", role: "clinician", practice_name: "",
    referral_code: new URLSearchParams(window.location.search).get("ref") || ""
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Clinician Well!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex" style={{ background: "var(--mf-bg)" }}>
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden items-center justify-center p-12" style={{ background: "var(--mf-primary)" }}>
        <div className="absolute inset-0 mf-noise opacity-30"></div>
        <div className="relative text-white max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-white p-0.5 flex items-center justify-center">
              <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-full h-full object-cover rounded-[inherit]" />
            </div>
            <span className="font-serif text-2xl">Clinician Well</span>
          </div>
          <h2 className="font-serif text-4xl leading-tight mb-4">Start reclaiming your evenings.</h2>
          <p className="opacity-85 leading-relaxed">All 10 core modules. AI notes. Telehealth. Intakes. In under 15 minutes.</p>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-6 overflow-y-auto">
        <div className="w-full max-w-md mf-fade-in py-8">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--mf-primary)" }}>
              <img src="/icons/icon-192.png" alt="Clinician Well logo" className="w-full h-full object-cover rounded-[inherit]" />
            </div>
            <span className="font-serif text-2xl" style={{ color: "var(--mf-primary)" }}>Clinician Well</span>
          </div>
          <h1 className="font-serif text-4xl mb-2">Create your account</h1>
          <p className="text-sm mb-7" style={{ color: "var(--mf-text-muted)" }}>5-step setup. No credit card required.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm mb-1.5 font-medium">Full name</label>
              <input required value={form.full_name} onChange={set("full_name")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-surface)", color: "var(--mf-text)" }} data-testid="register-name-input" />
            </div>
            <div>
              <label className="block text-sm mb-1.5 font-medium">Practice name</label>
              <input value={form.practice_name} onChange={set("practice_name")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-surface)", color: "var(--mf-text)" }} data-testid="register-practice-input" placeholder="Solo, or your clinic" />
            </div>
            <div>
              <label className="block text-sm mb-1.5 font-medium">Email</label>
              <input required type="email" value={form.email} onChange={set("email")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-surface)", color: "var(--mf-text)" }} data-testid="register-email-input" />
            </div>
            <div>
              <label className="block text-sm mb-1.5 font-medium">Password</label>
              <input required type="password" value={form.password} onChange={set("password")} minLength={8} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-surface)", color: "var(--mf-text)" }} data-testid="register-password-input" />
              <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Minimum 8 characters.</div>
            </div>
            <div>
              <label className="block text-sm mb-1.5 font-medium">Role</label>
              <select value={form.role} onChange={set("role")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-surface)", color: "var(--mf-text)" }} data-testid="register-role-select">
                <option value="clinician">Clinician</option>
                <option value="admin">Admin</option>
                <option value="supervisor">Supervisor</option>
                <option value="intern">Intern</option>
                <option value="billing">Billing Staff</option>
              </select>
            </div>
            {form.referral_code && (
              <div className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2" style={{ background: "rgba(201,168,76,0.15)", color: "#8a6f20" }} data-testid="register-ref-banner">
                Referred by code <code className="font-mono px-1">{form.referral_code}</code> · You'll get a free month
              </div>
            )}
            <button type="submit" disabled={loading} className="mf-btn-primary w-full" data-testid="register-submit-btn">
              {loading ? "Creating account…" : "Create Account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-center" style={{ color: "var(--mf-text-muted)" }}>
            Already have an account? <Link to="/login" className="font-medium" style={{ color: "var(--mf-primary)" }} data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
